import React, { useState } from "react";
import { Edit, useForm } from "@refinedev/antd";
import { Form, Input, Select, Upload, Button, message, Space, Card } from "antd";
import { UploadOutlined, FileTextOutlined } from "@ant-design/icons";
import { useUpdate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { DOCUMENT_TEMPLATE_ONE_QUERY, UPDATE_DOCUMENT_TEMPLATE_MUTATION } from "./queries";
import { IDocumentTemplate } from "./interface";

const deriveFileApiBase = () => {
  return import.meta.env.VITE_API_URL ||"https://buyapi.truwrk.lo/api";
};

const fileUploadUrl = `${deriveFileApiBase()}/file-storage/upload`;

export const DocumentTemplateEdit: React.FC = () => {
  const { formProps, saveButtonProps, queryResult } = useForm<IDocumentTemplate>({
    resource: "documentTemplates",
    meta: {
      gqlQuery: DOCUMENT_TEMPLATE_ONE_QUERY,
      gqlMutation: UPDATE_DOCUMENT_TEMPLATE_MUTATION,
    },
  });
  const { mutate: updateDocumentTemplate } = useUpdate();
  const navigate = useNavigate();
  const [uploadedFile, setUploadedFile] = useState<any>(null);

  const record = queryResult?.data?.data;

  const handleFinish = async (values: any) => {
    try {
      const updateData: any = {
        documentName: values.documentName,
        category: values.category,
        tag: values.tag,
      };

      // If a new file was uploaded, include the new fileId and url
      if (uploadedFile) {
        updateData.fileId = uploadedFile.fileId;
        updateData.url = uploadedFile.url;
      }

      await updateDocumentTemplate({
        resource: "documentTemplates",
        id: record?.id || "",
        values: updateData,
        meta: {
          gqlMutation: UPDATE_DOCUMENT_TEMPLATE_MUTATION,
        },
      });
      message.success("Document template updated successfully");
      navigate("/document-templates");
    } catch (error) {
      message.error("Failed to update document template");
    }
  };

  const uploadProps = {
    name: "file",
    customRequest: async (options: any) => {
      const { file, onSuccess, onError } = options;
      try {
        const fd = new FormData();
        fd.append("file", file as Blob);
        const resp = await fetch(fileUploadUrl, {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(txt || `Upload failed: ${resp.status}`);
        }
        const json = await resp.json();
        const fileData = json?.data;
        if (!fileData || !fileData.fileId) {
          throw new Error("Upload response missing file data or fileId");
        }
        setUploadedFile(fileData);
        onSuccess?.(json, file);
        message.success("New file uploaded successfully");
      } catch (e: any) {
        console.error(e);
        message.error(e?.message || "Upload failed");
        onError?.(e);
      }
    },
    showUploadList: false,
    accept: ".docx,.pdf,.odt,.ods,.xlsx,.xml,.md,.txt,.doc",
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    message.success("New file removed");
  };

  return (
    <Edit saveButtonProps={saveButtonProps} title="Edit Document Template">
      <Form {...formProps} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="documentName"
          label="Document Name"
          rules={[{ required: true, message: "Please enter document name" }]}
        >
          <Input placeholder="Enter document name" />
        </Form.Item>

        <Form.Item
          name="category"
          label="Category"
          rules={[{ required: true, message: "Please select category" }]}
        >
          <Select placeholder="Select category">
            <Select.Option value="CONTRACT">Contract</Select.Option>
            <Select.Option value="AGREEMENT">Agreement</Select.Option>
            <Select.Option value="POLICY">Policy</Select.Option>
            <Select.Option value="FORM">Form</Select.Option>
            <Select.Option value="REPORT">Report</Select.Option>
            <Select.Option value="INVOICE">Invoice</Select.Option>
            <Select.Option value="OTHER">Other</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="tag"
          label="Tag"
          rules={[{ required: true, message: "Please select tag" }]}
        >
          <Select placeholder="Select tag">
            <Select.Option value="LEGAL">Legal</Select.Option>
            <Select.Option value="FINANCE">Finance</Select.Option>
            <Select.Option value="HR">HR</Select.Option>
            <Select.Option value="PROCUREMENT">Procurement</Select.Option>
            <Select.Option value="ADMIN">Admin</Select.Option>
            <Select.Option value="GENERAL">General</Select.Option>
          </Select>
        </Form.Item>

        <Card
          title="Template File"
          style={{ marginTop: 16 }}
          extra={
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>
                Upload New File (Optional)
              </Button>
            </Upload>
          }
        >
          {uploadedFile ? (
            <div style={{ padding: 16 }}>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Space>
                  <FileTextOutlined style={{ fontSize: '18px', color: '#52c41a' }} />
                  <span style={{ fontWeight: 500, color: '#52c41a' }}>
                    New file uploaded: {uploadedFile.originalName}
                  </span>
                </Space>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  File ID: {uploadedFile.fileId}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Size: {((uploadedFile.size || 0) / 1024).toFixed(2)} KB
                </div>
                <Button
                  size="small"
                  danger
                  onClick={handleRemoveFile}
                  style={{ marginTop: 8 }}
                >
                  Remove New File
                </Button>
              </Space>
            </div>
          ) : record?.url ? (
            <div style={{ padding: 16 }}>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Space>
                  <FileTextOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                  <a href={record.url} target="_blank" rel="noreferrer" style={{ fontWeight: 500 }}>
                    Current file: {record.originalName || 'Template file'}
                  </a>
                </Space>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  File ID: {record.fileId}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: 8 }}>
                  Upload a new file above to replace the current template file
                </div>
              </Space>
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
              <FileTextOutlined style={{ fontSize: '24px', marginBottom: 8 }} />
              <div>No template file available</div>
              <div style={{ fontSize: '12px' }}>Upload a new file to add a template</div>
            </div>
          )}
        </Card>
      </Form>
    </Edit>
  );
};